/**
 * The shapes both sides agree on, before any of them mean anything.
 *
 * `Money` crosses the wire as a `bigint` and superjson carries it natively, so
 * there is no string encoding to get wrong on one side. That is the reason
 * superjson is the transformer rather than JSON — the old tree already used it
 * for `Date`, and money is the second thing that must not become a `number` in
 * transit.
 */
import { z } from 'zod'
import type { Money } from '@mgc/domain'

/*
 * No `new Function` where a Content-Security-Policy is watching.
 *
 * Zod 4 compiles a fast path for each schema and probes for it with
 * `new Function`. The kiosk's CSP (`index.html`) has no `script-src` and no
 * `'unsafe-eval'`, so `default-src 'self'` governs the evaluation and refuses
 * it. Zod catches the throw and falls back — nothing breaks — but the browser
 * reports the refusal before the catch, so every schema in this package logged
 * a CSP violation as the module loaded, which is what a console full of
 * `catalogue.ts:13 … 'script-src' was not explicitly set` was.
 *
 * `jitless` is zod's own answer: it skips the probe entirely rather than
 * paying for a refusal on the way to the same interpreted path. Set here
 * because every schema module in this package imports this one, and it must
 * run before the first `z.…` call. Guarded on the browser — `'document' in
 * globalThis` rather than the name, since this package does not take the DOM
 * lib — because the server has no CSP and the compiled path is worth having
 * there.
 */
if ('document' in globalThis) z.config({ jitless: true })

/** A `Money` on the wire. Refuses a `number` rather than coercing one. */
export const zMoney = z.bigint().transform((v) => v as Money)

/**
 * A product id or a 1C SKU.
 *
 * 128 rather than 64 because a product id is now the surrogate
 * `<branchId>:<sku>` — see `packages/db/src/schema/catalogue.ts` — so the
 * ceiling has to hold a branch id and a SKU with room to spare. The raw SKU
 * that travels back to 1C is still comfortably inside the old bound.
 */
export const zSku = z.string().min(1).max(128)
export const zId = z.string().min(1).max(128)
/** An ISO instant. The kiosk mints these, so it says when, not the server. */
export const zInstant = z.iso.datetime({ offset: true })

/**
 * A trading day, in the branch's own timezone. `YYYY-MM-DD`.
 *
 * **Text and not an instant, and the four reasons are in order of weight.**
 *
 * 1. A trading day is a *label*, not a moment. `branch_day_sequence.businessDate`
 *    is already `text` for exactly this reason, and a second representation of
 *    one concept is how the two drift.
 * 2. With more than one branch in range there **is** no single instant that
 *    means «7 September» — there are N, one per timezone, and only the label
 *    means the same thing in all of them.
 * 3. A console's clock is the operator's laptop. An admin in Berlin and a
 *    manager in Ulaanbaatar asking for the 7th must get the same rows; an
 *    instant makes the answer depend on where the browser is.
 * 4. A `Date` inside a raw `sql` template is not something postgres.js can
 *    encode — it throws `The "string" argument must be of type string`, which
 *    arrives as a failed query rather than as a type error, so it compiles and
 *    fails only against a real database. D39 records the same trap.
 *
 * `services/orders.ts`'s `businessDate()` produces exactly this string.
 */
export const zBusinessDate = z.iso.date()

export const zOrderType = z.enum(['eat_in', 'take_out'])
export const zTemperature = z.enum(['hot', 'ice'])
export const zPaymentMethod = z.enum(['cash', 'card', 'qpay'])
export const zPaymentState = z.enum(['pending', 'captured', 'failed', 'cancelled'])

export type OrderTypeWire = z.infer<typeof zOrderType>
export type PaymentMethodWire = z.infer<typeof zPaymentMethod>
